import loginLogo from '../../assets/images/adas-intelligence-login-logo.svg'
import { CheckCircle2, CircleDot, Eye, EyeOff, Loader2 } from 'lucide-react'
import qs from 'query-string'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { passwordResetAuth, passwordResetTokenValidation } from '../../api/api'

const lowerCaseLetters = /[a-z]/g
const upperCaseLetters = /[A-Z]/g
const numbers = /[0-9]/g
const specialCharRegex = /^[A-Za-z0-9 ]+$/

export const ResetPasswordForm = () => {
  const [isPasswordResetSuccess, setIsPasswordResetSuccess] = useState(false)
  const [showPassword, setShowPassword] = React.useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryObj = qs.parse(window.location.search)
  const token = queryObj ? queryObj.token : null
  const history = useHistory()
  const [isTokenValid, setIsTokenValid] = useState(true)

  const requirements = [
    { label: 'At least 8 characters and less than 22', met: newPassword.length >= 8 && newPassword.length <= 21 },
    { label: 'A lowercase letter', met: !!newPassword.match(lowerCaseLetters) },
    { label: 'An uppercase letter', met: !!newPassword.match(upperCaseLetters) },
    { label: 'A number', met: !!newPassword.match(numbers) },
    { label: 'A special character (!, #, $, ?)', met: newPassword.length > 0 && !newPassword.match(specialCharRegex) }
  ]
  const metCount = requirements.filter((r) => r.met).length
  const isPasswordValid = metCount === requirements.length

  const handleInputChange = (e) => {
    setNewPassword(e.target.value)
  }

  const submitHandler = (event) => {
    event.preventDefault()
    if (!isPasswordValid || isSubmitting) return
    setIsSubmitting(true)
    passwordResetAuth({ password: newPassword, token })
      .then((response) => {
        setIsPasswordResetSuccess(response.status === 200)
      })
      .catch(() => {
        setIsPasswordResetSuccess(false)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  useEffect(() => {
    passwordResetTokenValidation({ token })
      .then((response) => {
        setIsTokenValid(response.status === 200 ? response.data : false)
      })
      .catch(() => {
        // A 401/expired/missing token rejects the request — surface the invalid-request state.
        setIsTokenValid(false)
      })
  }, [token])

  const login = () => {
    history.push('/authenticate')
  }

  return (
    <div
      className='flex min-h-screen w-full items-center justify-center'
      style={{
        background: 'linear-gradient(135deg, #0A1628 0%, #0F2035 40%, #132B45 70%, #0A1628 100%)',
        backgroundSize: 'cover'
      }}
    >
      {/* Subtle hex pattern overlay */}
      <div
        className='pointer-events-none fixed inset-0 opacity-[0.06]'
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100' viewBox='0 0 56 100'%3E%3Cpath d='M28 66L0 50L0 16L28 0L56 16L56 50L28 66ZM28 100L0 84L0 50L28 34L56 50L56 84L28 100Z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      <div className='relative z-10 w-full max-w-[420px] px-4'>
        {/* Glass card */}
        <div className='rounded-2xl border border-white/[0.08] bg-white/[0.04] p-8 shadow-2xl backdrop-blur-md'>
          {/* Logo */}
          <div className='mb-6 flex flex-col items-center'>
            <img src={loginLogo} alt='ADAS Intelligence' width={180} style={{ height: 'auto', display: 'block' }} />
          </div>

          {!isTokenValid ? (
            <div className='text-center'>
              <h1 className='text-[22px] font-bold text-white'>Reset your password</h1>
              <p className='mt-4 text-[13px] leading-relaxed text-white/60'>
                This password reset request is invalid.
                <br />
                <br />
                Please return to the sign in page and select &quot;Forgot password?&quot; to generate a new request.
              </p>
              <button
                type='button'
                onClick={login}
                className='mt-6 text-[13px] font-medium text-white/70 transition-colors hover:text-white'
              >
                Back to Sign In
              </button>
            </div>
          ) : !isPasswordResetSuccess ? (
            <form onSubmit={submitHandler}>
              {/* Title */}
              <div className='mb-6 text-center'>
                <h1 className='text-[22px] font-bold text-white'>Reset your password</h1>
                <p className='mt-1 text-[13px] text-white/60'>Choose a new password for your account.</p>
              </div>

              {/* New password input */}
              <div className='relative'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={handleInputChange}
                  placeholder='New password'
                  autoComplete='new-password'
                  className='h-12 w-full rounded-full bg-[#E6E9EF] pl-5 pr-12 text-sm text-[#0A1628] placeholder:text-[#6B7280] outline-none transition focus:ring-2 focus:ring-[#1F6FEB]/50'
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseDown={(evt) => evt.preventDefault()}
                  aria-label='Toggle password visibility'
                  className='absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] transition-colors hover:text-[#0A1628]'
                >
                  {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                </button>
              </div>

              {/* Password requirements */}
              <div className='mt-5'>
                <div className='flex items-center justify-between'>
                  <span className='text-[11px] font-bold uppercase tracking-wider text-white/50'>Password Requirements</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                      isPasswordValid ? 'bg-[#16803F]/20 text-[#4ADE80]' : 'bg-white/10 text-white/60'
                    }`}
                  >
                    {metCount}/{requirements.length}
                  </span>
                </div>
                <ul className='mt-3 space-y-2'>
                  {requirements.map((req) => (
                    <li key={req.label} className='flex items-center gap-2.5'>
                      {req.met ? (
                        <CheckCircle2 className='h-4 w-4 flex-none text-[#4ADE80]' />
                      ) : (
                        <CircleDot className='h-4 w-4 flex-none text-white/30' />
                      )}
                      <span className={`text-[13px] ${req.met ? 'text-white/90' : 'text-white/60'}`}>{req.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Submit */}
              <button
                type='submit'
                disabled={!isPasswordValid || isSubmitting}
                className={`mt-6 flex h-12 w-full items-center justify-center rounded-full text-sm font-bold uppercase tracking-widest transition-all ${
                  isPasswordValid && !isSubmitting
                    ? 'bg-gradient-to-r from-[#1F6FEB] to-[#1F6FEB] text-white shadow-lg shadow-blue-500/25 hover:brightness-110 active:scale-[0.98]'
                    : 'cursor-not-allowed bg-white/[0.08] text-white/40'
                }`}
              >
                {isSubmitting ? <Loader2 className='h-5 w-5 animate-spin' /> : 'Reset Password'}
              </button>

              {/* Back to Sign In */}
              <div className='mt-5 text-center'>
                <button type='button' onClick={login} className='text-[13px] font-medium text-white/70 transition-colors hover:text-white'>
                  Back to Sign In
                </button>
              </div>
            </form>
          ) : (
            <div className='text-center'>
              <h1 className='text-[22px] font-bold text-white'>Password updated</h1>
              <p className='mt-2 text-[13px] text-white/60'>Your new password has been set successfully.</p>
              <button
                type='button'
                onClick={login}
                className='mt-6 text-[13px] font-medium text-[#60A5FA] transition-colors hover:text-[#93C5FD]'
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
